import { RiSortAsc, RiSortDesc } from '@remixicon/react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

export type TableHeaderCellType = 'default' | 'lead';

const DefaultTableHeaderCellType: TableHeaderCellType = 'default' as const;

/**
 *	Figma's `hover` is CSS, and its `active` is `sortActive`: the column the
 *	table is sorted by.
 */
export type TableHeaderCellState =
	| 'enabled'
	| 'disabled'
	| 'warning'
	| 'success'
	| 'error';

const DefaultTableHeaderCellState: TableHeaderCellState = 'enabled' as const;

export type TableHeaderCellAlign = 'left' | 'right';

const DefaultTableHeaderCellAlign: TableHeaderCellAlign = 'left' as const;

export type TableHeaderCellScope = 'col' | 'row';

const DefaultTableHeaderCellScope: TableHeaderCellScope = 'col' as const;

export type TableSortOrder = 'ascending' | 'descending';

const DefaultTableSortOrder: TableSortOrder = 'descending' as const;

type TableHeaderCellEventHandlers = {
	[K in keyof ComponentProps<'th'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'th'>[K];
};

interface TableHeaderCellDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TableHeaderCellProperties
	extends TableHeaderCellEventHandlers, TableHeaderCellDataAttributes {
	type?: TableHeaderCellType;
	state?: TableHeaderCellState;
	align?: TableHeaderCellAlign;

	/** `row` for a header that names the cells beside it rather than the column under it. */
	scope?: TableHeaderCellScope;

	/** Takes only the width its label needs, so the next column keeps the rest. */
	shrink?: boolean;

	label?: string;

	/** A control in place of the label, such as the lead column's checkbox. */
	content?: ReactNode;

	sortOrder?: TableSortOrder;
	/** Whether the table is sorted by this column. */
	sortActive?: boolean;
	/**
	 *	Makes the header a button with a sort arrow. The header does not reorder
	 *	anything: the caller does, then passes `sortOrder` and `sortActive` back.
	 */
	onSort?: () => void;
}

export function TableHeaderCell({
	type = DefaultTableHeaderCellType,
	state = DefaultTableHeaderCellState,
	align = DefaultTableHeaderCellAlign,
	scope = DefaultTableHeaderCellScope,
	shrink = false,

	label,

	content,

	sortOrder = DefaultTableSortOrder,
	sortActive = false,
	onSort,

	...props
}: TableHeaderCellProperties): ReactNode {
	const sortable: boolean = onSort !== undefined;

	const className: string = cn(
		css.cell,
		css[type],
		css[state],
		css[align],
		shrink && css.shrink,
		sortable && css.sortable,
		sortActive && css.active
	);

	const SortIcon = sortOrder === 'ascending' ? RiSortAsc : RiSortDesc;

	return (
		<th
			{...props}
			scope={scope}
			aria-sort={sortActive ? sortOrder : undefined}
			data-type={type}
			data-state={state}
			data-align={align}
			className={className}>
			{sortable ? (
				<button
					type="button"
					className={css.inner}
					disabled={state === 'disabled'}
					onClick={onSort}>
					<span className={css.label}>{label}</span>
					<SortIcon className={css.sort} aria-hidden />
				</button>
			) : (
				<div className={css.inner}>
					{content ?? <span className={css.label}>{label}</span>}
				</div>
			)}
		</th>
	);
}
